// src/pages/ProjectList.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import Sidebar from "../../components/Sidebar";
import Nav from "../../components/Nav";
import Toast from "../../utils/Toast";
import AddProject from "./AddProject";
import AccessRestricted from "../../components/AccessRestricted";
import { formatCurrency } from "../../utils/format";

import { FETCH_PROJECTS_REQUEST } from "../../store/Actions/Actions";
import { PROJECT_DETAILS } from "../../config/API_Collection";

import "../../styles/ProjectList.css";
import SearchableSelect from "./SearchableSelect";
import ProjectsTableBody from "./ProjectsTableBody";
import ProjectsPagination from "./PaginationControls";

/* ----------------------------- Helper layouts ----------------------------- */
function FullPageLoaderLayout() {
  return (
    <div style={{ display: "flex", height: "100vh" }}>
      <Sidebar />
      <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <Nav />
        <div className="top-loading-bar" />
        <div className="project-list-container">
          <div className="loading-wrapper">
            <div className="spinner-border text-secondary" role="status" />
          </div>
        </div>
      </div>
    </div>
  );
}

function RestrictedLayout() {
  return (
    <div style={{ display: "flex", height: "100vh" }}>
      <Sidebar />
      <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <Nav />
        <div className="h-100">
          <AccessRestricted homePath="clients" />
        </div>
      </div>
    </div>
  );
}

/* ----------------------------- Status normalizer ----------------------------- */
const getProjectStatusText = (p) => {
  const raw =
    p?.status_text ??
    p?.status_label ??
    p?.project_status_text ??
    p?.project_status ??
    p?.status ??
    "";
  if (typeof raw === "number") {
    switch (raw) {
      case 1:
        return "Active";
      case 2:
        return "Completed";
      case 3:
        return "On Hold";
      case 4:
        return "Blocked";
      case 0:
        return "Pending";
      default:
        return String(raw);
    }
  }
  return String(raw || "").trim();
};

const ProjectList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Auth / permissions
  const loginState = useSelector((s) => s.login);
  const actions = loginState?.actions || null;
  const isBootstrapping = !!loginState?.isBootstrapping;

  const canListProjects = actions?.projects?.project_list === true;
  const canCreateProject = actions?.projects?.new_project === true;

  // Redux data
  const isProjectsLoading = useSelector((s) => s?.project?.isLoading);
  const projects = useSelector((s) => s?.project?.projects) || [];

  // Local state
  const [toast, setToast] = useState({ message: "", type: "" });
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [projectsPerPage, setProjectsPerPage] = useState(100); // Default 100 projects per page
  const [showModal, setShowModal] = useState(false);
  const [update, setUpdate] = useState(false);

  // Dropdown filters
  const [filterManager, setFilterManager] = useState("All");
  const [filterClient, setFilterClient] = useState("All");
  const [filterStatus, setFilterStatus] = useState("All");

  // Spinner guard
  const [triggeredFetch, setTriggeredFetch] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  useEffect(() => {
    if (triggeredFetch && !isProjectsLoading) setHasLoadedOnce(true);
  }, [triggeredFetch, isProjectsLoading]);

  useEffect(() => {
    if (Array.isArray(projects)) setHasLoadedOnce(true);
  }, [projects]);

  const showProjectsSpinner =
    triggeredFetch && !hasLoadedOnce && isProjectsLoading;

  // Fetch
  useEffect(() => {
    if (canListProjects) {
      setTriggeredFetch(true);
      setHasLoadedOnce(false);
      dispatch({ type: FETCH_PROJECTS_REQUEST });
    }
  }, [dispatch, canListProjects, update]);

  // Build dropdown options from API data
  const { managerOptions, clientOptions, statusOptions } = useMemo(() => {
    const managersSet = new Set();
    const clientsSet = new Set();
    const statusesSet = new Set();

    projects.forEach((p) => {
      const first = (p?.project_manager_first_name || "").trim();
      const last = (p?.project_manager_last_name || "").trim();
      const full = `${first} ${last}`.trim();
      if (full) managersSet.add(full);

      const client = (p?.client || "").trim();
      if (client) clientsSet.add(client);

      const statusText = getProjectStatusText(p);
      if (statusText) statusesSet.add(statusText);
    });

    const toSortedArray = (set) =>
      Array.from(set).sort((a, b) => a.localeCompare(b));

    return {
      managerOptions: toSortedArray(managersSet),
      clientOptions: toSortedArray(clientsSet),
      statusOptions: toSortedArray(statusesSet),
    };
  }, [projects]);

  // Combined text + dropdown filters
  const filteredProjects = useMemo(() => {
    const term = (searchTerm || "").trim().toLowerCase();

    return projects.filter((p) => {
      const pmFirst = (p?.project_manager_first_name || "").trim();
      const pmLast = (p?.project_manager_last_name || "").trim();
      const pmFull = `${pmFirst} ${pmLast}`.trim();
      const client = (p?.client || "").trim();
      const statusText = getProjectStatusText(p);

      if (filterManager !== "All" && pmFull !== filterManager) return false;
      if (filterClient !== "All" && client !== filterClient) return false;
      if (filterStatus !== "All" && statusText !== filterStatus) return false;

      if (!term) return true;

      const projectName = (p?.project_name || "").toLowerCase();
      const clientName = (client || "").toLowerCase();
      const codeRaw = String(p?.project_code ?? "");
      const projectIdPretty = `prj${codeRaw}`.toLowerCase();
      const pmFullLower = pmFull.toLowerCase();

      return (
        projectName.includes(term) ||
        clientName.includes(term) ||
        projectIdPretty.includes(term) ||
        codeRaw.toLowerCase().includes(term) ||
        pmFullLower.includes(term) ||
        pmFirst.toLowerCase().includes(term) ||
        pmLast.toLowerCase().includes(term)
      );
    });
  }, [projects, searchTerm, filterManager, filterClient, filterStatus]);

  // Pagination
  const totalPages =
    Math.ceil((filteredProjects?.length || 0) / projectsPerPage) || 1;
  const indexOfLast = currentPage * projectsPerPage;
  const indexOfFirst = indexOfLast - projectsPerPage;
  const currentProjects = filteredProjects.slice(indexOfFirst, indexOfLast);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  // Build a condensed page list: show `1 2 3 ... last` when there are many pages
  const buildPageButtons = (current, total) => {
    if (total <= 3) return Array.from({ length: total }, (_, i) => i + 1);

    // If we're on the first two pages keep the original behaviour
    if (current <= 2) return [1, 2, "...", total];

    // If we're near the end, show the last three pages
    if (current >= total - 1) {
      const start = Math.max(1, total - 2);
      return Array.from({ length: total - start + 1 }, (_, i) => start + i);
    }

    // Default: show current as first, next page, ellipsis, last
    return [current, current + 1, "...", total];
  };
  const pageButtons = buildPageButtons(currentPage, totalPages);

  const showToast = (message, type) => setToast({ message, type });
  const handleOpenModal = () => setShowModal(true);
  const handleCloseModal = () => setShowModal(false);

  const projectDetail = (projectId) =>
    navigate(`${PROJECT_DETAILS}${projectId}`);

  // Render gates
  if (isBootstrapping) return <FullPageLoaderLayout />;
  if (!canListProjects) return <RestrictedLayout />;

  return (
    <div style={{ display: "flex", height: "100vh" }}>
      <Sidebar />
      <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <Nav />
        {showProjectsSpinner && <div className="top-loading-bar" />}
        <Toast message={toast.message} type={toast.type} />

        <div className="project-list-container">
          {/* Filters row (no horizontal scrollbar; wraps on smaller widths) */}
          <div className="row g-2 align-items-center mb-3">
            <div className="col-12 col-lg-3">
              <input
                type="text"
                className="form-control"
                placeholder="Search "
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>

            <div className="col-12 col-sm-6 col-lg-2">
              <SearchableSelect
                label="All Project Managers"
                options={managerOptions}
                value={filterManager}
                onChange={(v) => {
                  setFilterManager(v);
                  setCurrentPage(1);
                }}
                className="w-100"
                buttonClassName=""
              />
            </div>

            <div className="col-12 col-sm-6 col-lg-2">
              <SearchableSelect
                label="All Status"
                options={statusOptions}
                value={filterStatus}
                onChange={(v) => {
                  setFilterStatus(v);
                  setCurrentPage(1);
                }}
                className="w-100"
              />
            </div>

            <div className="col-12 col-sm-6 col-lg-2">
              <SearchableSelect
                label="All Clients"
                options={clientOptions}
                value={filterClient}
                onChange={(v) => {
                  setFilterClient(v);
                  setCurrentPage(1);
                }}
                className="w-100"
              />
            </div>

            <div className="col-6 col-lg-1 d-grid">
              <button
                type="button"
                className="btn btn-link text-secondary p-0 small"
                onClick={() => {
                  setSearchTerm("");
                  setFilterManager("All");
                  setFilterClient("All");
                  setFilterStatus("All");
                  setCurrentPage(1);
                }}
              >
                Reset Filters
              </button>
            </div>

            <div className="col-6 col-lg-2 d-grid">
              {canCreateProject && (
                <button
                  className="btn-sm btn-primary d-flex align-items-center justify-content-center p-2"
                  onClick={handleOpenModal}
                >
                  <i className="bi bi-plus-circle me-1" />
                  Add Project
                </button>
              )}
            </div>
          </div>

          {showProjectsSpinner ? (
            <div className="loading-wrapper">
              <div className="spinner-border text-secondary" role="status" />
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <table className="table table-bordered custom-table">
                  <thead>
                    <tr>
                      <th>Project ID</th>
                      <th>Project</th>
                      <th>Client Name</th>
                      <th>Project Manager</th>
                      <th>Budget ($)</th>
                      <th>Start Date</th>
                      <th>End Date</th>
                    </tr>
                  </thead>
                  <ProjectsTableBody
                    currentProjects={currentProjects}
                    projectDetail={projectDetail}
                    formatCurrency={formatCurrency}
                  />
                </table>
              </div>

              <ProjectsPagination
                filteredProjects={filteredProjects}
                projectsPerPage={projectsPerPage}
                currentPage={currentPage}
                totalPages={totalPages}
                pageButtons={pageButtons}
                handlePageChange={handlePageChange}
              />
              {/* Rows per page selector - bottom right */}
              <div className="d-flex justify-content-end align-items-center mt-3 gap-2">
                <span style={{ fontSize: '13px', color: '#6b7280' }}>Show</span>
                <select
                  className="form-select"
                  style={{ width: '100px', fontSize: '13px' }}
                  value={projectsPerPage === filteredProjects.length && filteredProjects.length > 500 ? "all" : projectsPerPage}
                  onChange={(e) => {
                    if (e.target.value === "all") {
                      setProjectsPerPage(filteredProjects.length);
                    } else {
                      setProjectsPerPage(Number(e.target.value));
                    }
                    setCurrentPage(1); // Reset to first page
                  }}
                >
                  <option value="100">100</option>
                  <option value="200">200</option>
                  <option value="500">500</option>
                  <option value="all">All</option>
                </select>
              </div>
            </>
          )}

          {/* Modal */}
          {showModal &&
            (canCreateProject ? (
              <div className="custom-project-modal">
                <div className="modal-backdrop fade show"></div>
                <div
                  className="modal fade show"
                  tabIndex="-1"
                  aria-labelledby="projectModalLabel"
                  aria-hidden="true"
                  style={{ display: "block", zIndex: 1050 }}
                >
                  <div
                    className="modal-dialog modal-lg mx-auto"
                    style={{ marginTop: "2%" }}
                  >
                    <div className="modal-content">
                      <div className="modal-header">
                        <h5 className="modal-title" id="projectModalLabel">
                          Create New Project
                        </h5>
                      </div>
                      <div className="modal-body">
                        <AddProject
                          closeModal={handleCloseModal}
                          showToast={showToast}
                          update={{
                            checkUpdate: update,
                            setCheckUpdate: setUpdate,
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <AccessRestricted homePath="clients" />
            ))}
        </div>
      </div>
    </div>
  );
};

export default ProjectList;
